import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl, Grid, Typography, Stack,
  Divider, Pagination, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../contexts/AuthContext';

const AUCTION_STATUS_COLOR = { active: 'success', pending: 'warning', closed: 'default', canceled: 'error', cancelled: 'error' };

const AdminAuctions = ({
  auctions,
  auctionsLoading,
  auctionsError,
  auctionsPage,
  auctionsTotalPages,
  auctionsSearch,
  setAuctionsSearch,
  products,
  showAuctionModal,
  editingAuction,
  auctionForm,
  auctionFormError,
  auctionFormLoading,
  openAuctionModal,
  closeAuctionModal,
  handleAuctionFormSubmit,
  handleAuctionFormChange,
  handleDeleteAuction,
  fetchAuctions,
  availablePackConfigs
}) => {
  const { token } = useAuth();
  const [historyRooms, setHistoryRooms] = useState([]);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('closed');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [selectedHistoryRoomId, setSelectedHistoryRoomId] = useState(null);
  const [historyDetailCache, setHistoryDetailCache] = useState({});
  const historyCacheRef = useRef(historyDetailCache);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);

  useEffect(() => {
    historyCacheRef.current = historyDetailCache;
  }, [historyDetailCache]);

  const historyFilterOptions = [
    { label: 'Closed', value: 'closed' },
    { label: 'Cancelled', value: 'cancelled' }
  ];

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const formatDateTime = (dateTime) => {
    if (!dateTime) return '—';
    const parsed = new Date(dateTime);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
  };

  const formatUserName = (user) => {
    if (!user) return 'Unknown user';
    const first = user.profile?.firstName || '';
    const last = user.profile?.lastName || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || user.username || 'Unknown user';
  };

  const statusPillClasses = (status) => {
    switch (status) {
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100';
      case 'cancelled':
      case 'canceled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/70 dark:text-red-200';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200';
    }
  };

  const fetchHistoryRooms = useCallback(async (statusValue = historyStatusFilter) => {
    if (!token) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const statusesToQuery = statusValue === 'cancelled' ? ['cancelled', 'canceled'] : [statusValue];
      const roomsMap = new Map();

      for (const statusParam of statusesToQuery) {
        const params = new URLSearchParams({ status: statusParam, limit: '30' }).toString();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          data.data.forEach(room => {
            roomsMap.set(room._id, room);
          });
        }
      }

      const rooms = Array.from(roomsMap.values());
      setHistoryRooms(rooms);
      if (rooms.length === 0) {
        setSelectedHistoryRoomId(null);
      } else {
        setSelectedHistoryRoomId(prev => (prev && roomsMap.has(prev) ? prev : rooms[0]._id));
      }
    } catch (error) {
      console.error('Error fetching auction history:', error);
      setHistoryError('Failed to load auction history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStatusFilter, token]);

  const fetchHistoryRoomDetails = useCallback(async (roomId) => {
    if (!roomId || !token || historyCacheRef.current[roomId]) return;
    setHistoryDetailLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auction-rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setHistoryDetailCache(prev => ({
          ...prev,
          [roomId]: data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching history room details:', error);
    } finally {
      setHistoryDetailLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistoryRooms(historyStatusFilter);
  }, [historyStatusFilter, fetchHistoryRooms]);

  useEffect(() => {
    if (selectedHistoryRoomId) {
      fetchHistoryRoomDetails(selectedHistoryRoomId);
    }
  }, [selectedHistoryRoomId, fetchHistoryRoomDetails]);

  const selectedHistorySummary = useMemo(
    () => historyRooms.find(room => room._id === selectedHistoryRoomId) || null,
    [historyRooms, selectedHistoryRoomId]
  );

  const selectedHistoryDetails = selectedHistoryRoomId
    ? historyDetailCache[selectedHistoryRoomId]
    : null;

  const selectedHistoryRoom = selectedHistoryDetails || selectedHistorySummary;

  const sortedHistoryBids = useMemo(() => {
    if (!selectedHistoryRoom?.bids) return [];
    return [...selectedHistoryRoom.bids].sort((a, b) => {
      const bTime = new Date(b.timestamp || b.createdAt || b.time || 0).getTime() || 0;
      const aTime = new Date(a.timestamp || a.createdAt || a.time || 0).getTime() || 0;
      return bTime - aTime;
    });
  }, [selectedHistoryRoom]);

  return (
    <Stack spacing={3}>
      {/* Main Auctions Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Normal Auctions"
          subheader="Manage auction rooms and their lifecycle"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
          action={
            <Stack direction="row" spacing={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchAuctions(auctionsPage)}><RefreshOutlinedIcon /></IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => openAuctionModal()} sx={{ borderRadius: 2 }}>
                Add Auction
              </Button>
            </Stack>
          }
        />
        {auctionsLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search auctions…"
              value={auctionsSearch}
              onChange={e => setAuctionsSearch(e.target.value)}
              sx={{ width: '100%' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Box>
          {auctionsError && <Alert severity="error" sx={{ mb: 2 }}>{auctionsError}</Alert>}
          <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 580 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                  <TableCell>Product</TableCell>
                  <TableCell>Min Bid</TableCell>
                  <TableCell>Bid Increment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auctions.map(auction => (
                  <TableRow key={auction._id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{auction.productId?.name || 'N/A'}</TableCell>
                    <TableCell>{auction.minBid}</TableCell>
                    <TableCell>{auction.increment}</TableCell>
                    <TableCell>
                      <Chip label={auction.status} size="small" color={AUCTION_STATUS_COLOR[auction.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => openAuctionModal(auction)} title="Edit">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <Tooltip title={auction.status === 'active' ? 'Cannot delete active auctions' : 'Delete auction'}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleDeleteAuction(auction)} disabled={auction.status === 'active'} title="Delete">
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!auctionsLoading && auctions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      <Stack alignItems="center" spacing={1}>
                        <GavelOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                        <Typography variant="body2">No auctions found</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {auctionsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={auctionsTotalPages} page={auctionsPage} onChange={(_, p) => fetchAuctions(p)} color="primary" shape="rounded" />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Auction History Card */}
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Auction Room History"
          subheader="Inspect completed rooms, winners, and bid trails"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        />
        {historyLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="flex-end" flexWrap="wrap" sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={historyStatusFilter}
              exclusive
              size="small"
              onChange={(_, v) => { if (v) { setSelectedHistoryRoomId(null); setHistoryStatusFilter(v); } }}
            >
              {historyFilterOptions.map(o => (
                <ToggleButton key={o.value} value={o.value} sx={{ borderRadius: 2, textTransform: 'capitalize' }}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton onClick={() => fetchHistoryRooms(historyStatusFilter)} size="small"><RefreshOutlinedIcon /></IconButton>
          </Stack>
          <Grid container spacing={2}>
            {/* Room list */}
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ borderRadius: 2, maxHeight: 520, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>Recent Rooms</Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                  {historyLoading ? (
                    <Stack spacing={1} sx={{ p: 1 }}>
                      {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={72} />)}
                    </Stack>
                  ) : historyRooms.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <HistoryOutlinedIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography variant="body2">No rooms for this status.</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {historyRooms.map(room => {
                        const isSelected = room._id === selectedHistoryRoomId;
                        return (
                          <Paper
                            key={room._id}
                            variant="outlined"
                            onClick={() => setSelectedHistoryRoomId(room._id)}
                            sx={{
                              p: 1.5, borderRadius: 2, cursor: 'pointer',
                              borderColor: isSelected ? 'primary.main' : 'divider',
                              bgcolor: isSelected ? 'action.selected' : 'background.paper',
                              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>{room.productId?.name || 'Unknown Product'}</Typography>
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">{formatDateTime(room.endTime)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {room.analytics?.participantCount || room.participants?.length || 0} participants
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Final Bid</Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {room.currentHighestBid?.amount ? formatPrice(room.currentHighestBid.amount) : '—'}
                              </Typography>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Room details */}
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ borderRadius: 2, minHeight: 420, p: 2.5 }}>
                {selectedHistoryRoom ? (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={1} sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{selectedHistoryRoom.productId?.name || 'Unknown Product'}</Typography>
                        <Typography variant="caption" color="text.secondary">{selectedHistoryRoom.productId?.category || 'Uncategorized'}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={selectedHistoryRoom.status} size="small" color={AUCTION_STATUS_COLOR[selectedHistoryRoom.status] || 'default'} />
                        {selectedHistoryRoom.currentHighestBid?.amount && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">Final Price</Typography>
                            <Typography variant="h6" fontWeight={700}>{formatPrice(selectedHistoryRoom.currentHighestBid.amount)}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Stack>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          {[
                            ['Start Time', formatDateTime(selectedHistoryRoom.startTime)],
                            ['End Time', formatDateTime(selectedHistoryRoom.endTime)],
                            ['Duration', selectedHistoryRoom.startTime && selectedHistoryRoom.endTime
                              ? `${Math.max(1, Math.round((new Date(selectedHistoryRoom.endTime) - new Date(selectedHistoryRoom.startTime)) / 60000))} minutes`
                              : '—']
                          ].map(([label, value]) => (
                            <Box key={label} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="body2" fontWeight={600}>{value}</Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          {[
                            ['Participants', selectedHistoryRoom.analytics?.participantCount ?? selectedHistoryRoom.participants?.length ?? 0],
                            ['Winner', selectedHistoryRoom.currentHighestBid?.userId ? formatUserName(selectedHistoryRoom.currentHighestBid.userId) : 'No winner'],
                            ['Participation Fee', formatPrice(selectedHistoryRoom.participationFee || 0)],
                            ['Min Bid / Increment', `${formatPrice(selectedHistoryRoom.minBid)} / ${formatPrice(selectedHistoryRoom.bidIncrement)}`]
                          ].map(([label, value]) => (
                            <Stack key={label} direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">{label}</Typography>
                              <Typography variant="caption" fontWeight={600}>{value}</Typography>
                            </Stack>
                          ))}
                        </Paper>
                      </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Bid History</Typography>
                      {historyDetailLoading && <Typography variant="caption" color="text.secondary">Refreshing…</Typography>}
                    </Stack>
                    {sortedHistoryBids.length > 0 ? (
                      <TableContainer sx={{ maxHeight: 280, overflowX: 'auto' }}>
                        <Table size="small" stickyHeader sx={{ minWidth: 360 }}>
                          <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' } }}>
                              <TableCell>Bidder</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Time</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedHistoryBids.map((bid, index) => (
                              <TableRow key={bid._id || bid.bidId || index} hover>
                                <TableCell>
                                  {formatUserName(bid.userId)}
                                  {bid.isAuto && <Chip label="Auto" size="small" sx={{ ml: 0.5 }} />}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{formatPrice(bid.amount || 0)}</TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{formatDateTime(bid.timestamp || bid.createdAt)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No bids recorded for this room.</Typography>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 360, color: 'text.secondary' }}>
                    <Stack alignItems="center" spacing={1}>
                      <HistoryOutlinedIcon sx={{ fontSize: 48, opacity: 0.2 }} />
                      <Typography variant="body2">Select a room to view its details.</Typography>
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Auction Form Dialog */}
      <Dialog open={showAuctionModal} onClose={closeAuctionModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelOutlinedIcon />
            {editingAuction ? 'Edit Auction' : 'Add Auction'}
          </Box>
          <IconButton onClick={closeAuctionModal} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleAuctionFormSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select name="productId" value={auctionForm.productId} label="Product" onChange={handleAuctionFormChange}>
                  <MenuItem value=""><em>Select a product…</em></MenuItem>
                  {products.length === 0 ? (
                    <MenuItem value="" disabled>Loading products…</MenuItem>
                  ) : (
                    products.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)
                  )}
                </Select>
              </FormControl>
              {auctionFormError && <Alert severity="error">{auctionFormError}</Alert>}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Min Bid" name="minBid" value={auctionForm.minBid} onChange={handleAuctionFormChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Bid Increment" name="bidIncrement" value={auctionForm.bidIncrement} onChange={handleAuctionFormChange} required inputProps={{ min: 0.1, step: 0.1 }} />
                </Grid>
              </Grid>

              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Pack Increment (bids per pack)</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>{auctionForm.increment}</Typography>
                  <Typography variant="caption" color="text.secondary">Derived from pack config</Typography>
                </Stack>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Pack Configuration</InputLabel>
                <Select name="packConfigId" value={auctionForm.packConfigId} label="Pack Configuration" onChange={handleAuctionFormChange}>
                  <MenuItem value="">Use Global Configuration</MenuItem>
                  {availablePackConfigs?.map(config => (
                    <MenuItem key={config._id} value={config._id}>
                      {config.scope === 'global' ? 'Global' : 'Custom'} — {config.strategy} (Base: {config.basePrice} TND
                      {config.strategy === 'exponential' && `, Factor: ${config.exponentialFactor}`}
                      {config.strategy === 'custom' && `, Tiers: ${config.customTiers?.length || 0}`})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="datetime-local" label="Start Time *" name="startTime" value={auctionForm.startTime} onChange={handleAuctionFormChange} required InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Duration (minutes) *" name="durationMinutes" value={auctionForm.durationMinutes} onChange={handleAuctionFormChange} required inputProps={{ min: 1 }} />
                </Grid>
              </Grid>

              {auctionForm.startTime && auctionForm.durationMinutes && (
                <Typography variant="caption" color="text.secondary">
                  Computed End Time: {new Date(new Date(auctionForm.startTime).getTime() + (parseInt(auctionForm.durationMinutes, 10) || 0) * 60000).toLocaleString()}
                </Typography>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select name="mode" value={auctionForm.mode} label="Mode" onChange={handleAuctionFormChange}>
                      <MenuItem value="fixed">Fixed Time</MenuItem>
                      <MenuItem value="auto-extend">Auto Extend</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select name="status" value={auctionForm.status} label="Status" onChange={handleAuctionFormChange}>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                      <MenuItem value="canceled">Canceled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {auctionForm.mode === 'auto-extend' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="number" label="Auto-Extend Duration (seconds)" name="autoExtendDuration" value={auctionForm.autoExtendDuration} onChange={handleAuctionFormChange} inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="number" label="Trigger Threshold (seconds remaining)" name="autoExtendTriggerSeconds" value={auctionForm.autoExtendTriggerSeconds} onChange={handleAuctionFormChange} inputProps={{ min: 1 }} />
                  </Grid>
                </Grid>
              )}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeAuctionModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={auctionFormLoading}>
              {auctionFormLoading ? 'Saving…' : (editingAuction ? 'Update Auction' : 'Add Auction')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
};

export default AdminAuctions;
